import { BookOpen, Layers, Trophy, Gem, type LucideIcon } from 'lucide-react';

export type LearnLesson = {
  key: string;
  title: string;
  /** Sub-tab inside the Learn tab where this lesson lives (used for deep linking) */
  subTab: 'howto' | 'inks' | 'decks' | 'strategy' | 'paths' | 'anatomy' | 'quiz' | 'simulator';
  /** Optional element anchor that the Index page can scroll to */
  anchor?: string;
  /** Short description shown in the path detail list */
  blurb?: string;
};

export type LearnPath = {
  slug: string;
  title: string;
  tagline: string;
  icon: LucideIcon;
  accent: string; // tailwind text color for the icon
  certificateTitle: string;
  lessons: LearnLesson[];
};

export const LEARN_PATHS: LearnPath[] = [
  {
    slug: 'rules-basics',
    title: 'Rules Basics',
    tagline: 'Turn structure, ink, lore, questing, and challenges.',
    icon: BookOpen,
    accent: 'text-primary',
    certificateTitle: 'Lorcana Apprentice',
    lessons: [
      { key: 'howto-step-0', title: 'Goal of the Game', subTab: 'howto', anchor: 'howto', blurb: 'Be the first to 20 lore.' },
      { key: 'howto-step-1', title: 'Setting Up', subTab: 'howto', anchor: 'howto', blurb: '60-card deck, opening hand, mulligan rules.' },
      { key: 'howto-step-2', title: 'Inking', subTab: 'howto', anchor: 'howto', blurb: 'How to grow your inkwell each turn.' },
      { key: 'howto-inkable', title: 'Inkable Border', subTab: 'howto', anchor: 'inkable', blurb: 'Silver swirl = inkable.' },
      { key: 'howto-non-inkable', title: 'Non-Inkable Border', subTab: 'howto', anchor: 'non-inkable', blurb: 'Plain border = cannot be inked.' },
      { key: 'howto-step-3', title: 'Playing Cards', subTab: 'howto', anchor: 'howto', blurb: 'Costs, exerted entries, action vs character.' },
      { key: 'howto-step-4', title: 'Questing', subTab: 'howto', anchor: 'howto', blurb: 'Exert characters to earn lore.' },
      { key: 'howto-lore', title: 'Reading Lore Value', subTab: 'howto', anchor: 'lore', blurb: 'The gold teardrop symbol.' },
      { key: 'howto-step-5', title: 'Challenging', subTab: 'howto', anchor: 'howto', blurb: 'Damage, willpower, banishment.' },
      { key: 'howto-step-6', title: 'Winning the Game', subTab: 'howto', anchor: 'howto', blurb: 'Race to 20 lore.' },
      { key: 'anatomy-explorer', title: 'Interactive Card Anatomy', subTab: 'anatomy', anchor: 'character', blurb: 'Tap every part of a card to learn what it does.' },
      { key: 'sim-first-turn', title: 'Sample Turn Simulator', subTab: 'simulator', blurb: 'Play through Ink → Play → Quest → Challenge step by step.' },
      { key: 'quiz-rules-basics', title: 'Rules Basics Quiz', subTab: 'quiz', anchor: 'rules-basics', blurb: 'Test yourself — pass to complete the path.' },
    ],
  },
  {
    slug: 'deckbuilding',
    title: 'Deckbuilding',
    tagline: 'Ink curves, two-ink combos, and win conditions.',
    icon: Layers,
    accent: 'text-ink-emerald',
    certificateTitle: 'Lorcana Architect',
    lessons: [
      { key: 'strategy-what-is-ink-curve', title: 'What Is Ink Curve?', subTab: 'strategy', blurb: 'Balance your mana costs for consistent turns.' },
      { key: 'strategy-picking-your-first-two-inks', title: 'Picking Your First Two Inks', subTab: 'strategy', blurb: 'Find a two-ink pair that fits how you play.' },
      { key: 'strategy-uninkable-cards-how-many', title: 'How Many Uninkable Cards?', subTab: 'strategy', blurb: 'The math behind the 8–12 uninkable rule of thumb.' },
      { key: 'strategy-building-around-a-win-condition', title: 'Building Around a Win Condition', subTab: 'strategy', blurb: 'Every card should serve your path to 20 lore.' },
      { key: 'quiz-deckbuilding', title: 'Deckbuilding Quiz', subTab: 'quiz', anchor: 'deckbuilding', blurb: 'Test yourself — pass to complete the path.' },
    ],
  },
  {
    slug: 'competitive-play',
    title: 'Competitive Play',
    tagline: 'Meta, mulligans, sideboarding, and tournament etiquette.',
    icon: Trophy,
    accent: 'text-ink-ruby',
    certificateTitle: 'Lorcana Competitor',
    lessons: [
      { key: 'strategy-when-to-quest-vs-challenge', title: 'When to Quest vs Challenge', subTab: 'strategy', blurb: 'The core decision of every turn.' },
      { key: 'strategy-reading-the-meta', title: 'Reading the Meta', subTab: 'strategy', blurb: 'Adapt to what people are actually playing.' },
      { key: 'strategy-board-state-assessment', title: 'Board State Assessment', subTab: 'strategy', blurb: 'Know who is winning, and by how much.' },
      { key: 'strategy-sideboarding-strategies', title: 'Sideboarding Strategies', subTab: 'strategy', blurb: 'Swap cards between games for the matchup.' },
      { key: 'strategy-tempo-card-advantage', title: 'Tempo & Card Advantage', subTab: 'strategy', blurb: 'Two of the most important advanced concepts.' },
      { key: 'quiz-competitive-play', title: 'Competitive Play Quiz', subTab: 'quiz', anchor: 'competitive-play', blurb: 'Test yourself — pass to complete the path.' },
    ],
  },
  {
    slug: 'trading-collecting',
    title: 'Trading & Collecting',
    tagline: 'Rarities, sets, pricing, sleeves, and storage.',
    icon: Gem,
    accent: 'text-ink-sapphire',
    certificateTitle: 'Lorcana Collector',
    lessons: [
      { key: 'collect-rarities', title: 'Card Rarities Explained', subTab: 'paths', blurb: 'Common, Uncommon, Rare, Super Rare, Legendary, Enchanted.' },
      { key: 'collect-sets', title: 'Sets & Rotation', subTab: 'paths', blurb: 'How sets release and what rotation means for collectors.' },
      { key: 'collect-pricing', title: 'How Pricing Works', subTab: 'paths', blurb: 'Demand, condition, and where to check prices.' },
      { key: 'collect-storage', title: 'Sleeves, Binders & Storage', subTab: 'paths', blurb: 'Keep cards safe and tradeable.' },
      { key: 'quiz-trading-collecting', title: 'Trading & Collecting Quiz', subTab: 'quiz', anchor: 'trading-collecting', blurb: 'Test yourself — pass to complete the path.' },
    ],
  },
];

export function findNextIncompleteLesson(
  isComplete: (key: string) => boolean,
): { path: LearnPath; lesson: LearnLesson } | null {
  for (const path of LEARN_PATHS) {
    const lesson = path.lessons.find((l) => !isComplete(l.key));
    if (lesson) return { path, lesson };
  }
  return null;
}

export function pathProgress(
  path: LearnPath,
  isComplete: (key: string) => boolean,
): { done: number; total: number; pct: number } {
  const total = path.lessons.length;
  const done = path.lessons.filter((l) => isComplete(l.key)).length;
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) };
}
